"use client"

import { useCallback, useEffect, useState } from "react"
import { useApi } from "@/lib/api"
import { coalesceWorkflowFetch } from "@/hooks/workflow-fetch-cache"
import type { StageConfiguration, WorkflowConfiguration } from "@/types/workflow"

/**
 * Sub-tab descriptor for the VMS tab. The VMS tab always shows two sub-tabs —
 * Requests and Emails — each its own entity type on the backend with its own
 * configuration document.
 */
export interface VmsSubTab {
  /** entityType passed to backend `/configurations` and used by search APIs. */
  entityType: "VmsRequest" | "VmsEmail"
  /** Single stage returned by the backend (we collapsed VMS to one "All" stage). */
  stage: StageConfiguration | null
  /** Full config object so the grid / context can use it. */
  configuration: WorkflowConfiguration | null
  /**
   * Total record count for this sub-tab's pill. Always null at fetch time —
   * both sub-tab grids mount up-front (the inactive one hidden), so each
   * pill's number arrives with its grid's own data search (@odata.count via
   * TotalCountReporter → filteredCounts override in vms/tab.tsx). No
   * dedicated top=0 count call is ever fired.
   */
  count: number | null
}

/**
 * Hook to fetch VMS-specific stage configurations: parallel GETs against
 * /api/workflow/v2/configurations for VmsRequest and VmsEmail.
 */
export function useVmsConfig() {
  const { workflowApi } = useApi()
  const [subTabs, setSubTabs] = useState<VmsSubTab[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchOne = useCallback(
    async (entityType: VmsSubTab["entityType"]): Promise<VmsSubTab> => {
      const params = new URLSearchParams({ entityType })
      const configUrl = `/api/workflow/v2/configurations?${params.toString()}`

      // Coalesce with the inner WorkflowProvider's mount-time fetch — both
      // hit the same URL within the same tick on initial paint, so sharing
      // one round-trip cuts a duplicate request out of every VMS visit.
      // Sort happens inside the fetcher so the shared resolved value is
      // already in display order for whichever consumer reads it first.
      const config = await coalesceWorkflowFetch(configUrl, async () => {
        const c = await workflowApi.get<WorkflowConfiguration>(configUrl)
        if (c && Array.isArray(c.stageConfigurations)) {
          c.stageConfigurations.forEach((stageConfig) => {
            if (Array.isArray(stageConfig.fieldConfigurations)) {
              stageConfig.fieldConfigurations.sort((a, b) => a.order - b.order)
            }
          })
          c.stageConfigurations.sort((a, b) => a.order - b.order)
        }
        return c
      })

      return {
        entityType,
        stage: config?.stageConfigurations?.[0] ?? null,
        configuration: config ?? null,
        count: null,
      }
    },
    [workflowApi]
  )

  const fetchVmsConfiguration = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const results = await Promise.allSettled([
        fetchOne("VmsRequest"),
        fetchOne("VmsEmail"),
      ])
      const tabs: VmsSubTab[] = results
        .map((r) => (r.status === "fulfilled" ? r.value : null))
        .filter((t): t is VmsSubTab => t !== null)
      setSubTabs(tabs)
      if (tabs.length === 0) {
        setError("Failed to fetch VMS configurations")
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to fetch VMS configurations"
      setError(msg)
      setSubTabs([])
    } finally {
      setIsLoading(false)
    }
  }, [fetchOne])

  useEffect(() => {
    fetchVmsConfiguration()
  }, [fetchVmsConfiguration])

  return {
    subTabs,
    isLoading,
    error,
    refetch: fetchVmsConfiguration,
  }
}
